import {createSlice} from '@reduxjs/toolkit'
import {ISavedSearchFE} from 'interfaces/ISavedSearchFE'
import {
  classifyTextWNotifySearch,
  classifyTextWRecentSearch,
  deleteSavedSearch,
  fetchSavedSearch,
  fetchSavedSearches,
  saveNewNotifySearch,
  updateRecentSavedSearch,
  updateSavedNotificationSearch,
} from 'redux/actions/savedSearchActions'

export interface SavedSearchState {
  savedSearch: ISavedSearchFE | undefined
  savedRecentSearches: ISavedSearchFE[] | undefined
  savedNotifySearches?: ISavedSearchFE[] | undefined
  savedMetadataSearches?: ISavedSearchFE[] | undefined
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
}

const initialState: SavedSearchState = {
  savedSearch: undefined,
  savedRecentSearches: undefined,
  savedNotifySearches: undefined,
  savedMetadataSearches: undefined,
  status: 'idle',
  error: null,
}

const savedSearchSlice = createSlice({
  name: 'savedSearchList',
  initialState,
  reducers: {
    clearSavedSearch: (state) => {
      state.savedSearch = undefined
    },
    updateRecentSearches: (state, action) => {
      state.savedRecentSearches = action.payload
    },
    saveSavedSearchAs: (state, action) => {
      state.savedSearch = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSavedSearch.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchSavedSearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.savedSearch = action.payload
      })
      .addCase(fetchSavedSearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(updateRecentSavedSearch.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(updateRecentSavedSearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.savedSearch = action.payload
      })
      .addCase(updateRecentSavedSearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(classifyTextWRecentSearch.pending, (state) => {
        state.status = 'loading'
      })
      //When we call classifyText it is either updating or adding a new search, which means
      //our recent searches has changed, so this thunkhook action returns all saved searches
      //we need to update the recent searches in this slice and also the saved search
      .addCase(classifyTextWRecentSearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.savedRecentSearches =
          action.payload?.recentSearches?.filter(
            (f) => f.is_metadata === false && f.is_notify === false
          ) ?? []
        state.savedSearch = action.payload?.savedSearch
      })
      .addCase(classifyTextWRecentSearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(classifyTextWNotifySearch.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(classifyTextWNotifySearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const orderNotify =
          action.payload?.notifySearches?.filter((f) => f.is_notify === true) ?? []
        orderNotify.sort((a, b) => {
          const bCreated = b.created?.at ?? 0
          const aCreated = a.created?.at ?? 0
          return new Date(bCreated.toString()).getTime() - new Date(aCreated.toString()).getTime()
        })
        state.savedNotifySearches = orderNotify
      })
      .addCase(classifyTextWNotifySearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(fetchSavedSearches.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchSavedSearches.fulfilled, (state, action) => {
        //     const recentSearches = response?.filter(
        //   (r) => r.project_id === null && r.is_metadata === false && r.is_notify === false
        // )
        state.status = 'succeeded'
        state.savedRecentSearches =
          action.payload?.filter(
            (r) => r.project_id === null && r.is_metadata === false && r.is_notify === false
          ) ?? []
        state.savedMetadataSearches =
          action.payload?.filter(
            (r) => r.project_id === null && r.is_metadata === true && r.is_notify === false
          ) ?? []
        const orderNotify =
          action.payload?.filter(
            (r) => r.project_id === null && r.is_metadata === false && r.is_notify === true
          ) ?? []
        orderNotify.sort((a, b) => {
          const bCreated = b.created?.at ?? 0
          const aCreated = a.created?.at ?? 0
          return new Date(bCreated.toString()).getTime() - new Date(aCreated.toString()).getTime()
        })
        state.savedNotifySearches = orderNotify
      })
      .addCase(fetchSavedSearches.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(deleteSavedSearch.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(deleteSavedSearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        //id of deleted search is returned so it gets filtered out of the recent searches
        state.savedRecentSearches = state.savedRecentSearches?.filter((f) => f.id != action.payload)
        state.savedMetadataSearches = state.savedMetadataSearches?.filter(
          (f) => f.id != action.payload
        )
        const orderTis = state.savedNotifySearches?.filter((f) => f.id != action.payload)
        orderTis?.sort((a, b) => {
          const bCreated = b.created?.at ?? 0
          const aCreated = a.created?.at ?? 0
          return new Date(bCreated.toString()).getTime() - new Date(aCreated.toString()).getTime()
        })
        state.savedNotifySearches = orderTis
      })
      .addCase(deleteSavedSearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(updateSavedNotificationSearch.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(updateSavedNotificationSearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        //update the notify searches with the new search
        const filtered = state.savedNotifySearches?.filter((f) => f.id !== action.payload?.id) ?? []
        const searchesToOrder = action.payload ? [...filtered, action.payload] : filtered
        searchesToOrder.sort((a, b) => {
          const bCreated = b.created?.at ?? 0
          const aCreated = a.created?.at ?? 0
          return new Date(bCreated.toString()).getTime() - new Date(aCreated.toString()).getTime()
        })
        //update the saved notify searches with the new search
        state.savedNotifySearches = searchesToOrder
        //update the recent searches with the new search
      })
      .addCase(updateSavedNotificationSearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(saveNewNotifySearch.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(saveNewNotifySearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        //add the new search to the notify searches
        const filtered = state.savedNotifySearches?.filter((f) => f.id !== action.payload?.id) ?? []
        const searchesToOrder = action.payload ? [...filtered, action.payload] : filtered
        searchesToOrder.sort((a: any, b: any) => {
          const bCreated = b.created_at ? b.created_at : b.created?.at ?? 0
          const aCreated = a.created_at ? a.created_at : a.created?.at ?? 0
          return new Date(bCreated.toString()).getTime() - new Date(aCreated.toString()).getTime()
        })
        //update the saved notify searches with the new search
        state.savedNotifySearches = searchesToOrder
      })
      .addCase(saveNewNotifySearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
  },
})

export const {clearSavedSearch, updateRecentSearches, saveSavedSearchAs} = savedSearchSlice.actions
export default savedSearchSlice.reducer
